import React from 'react'
import { Redirect } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Permission, hasPermission, hasAnyPermission } from '../auth0/auth0'
import RootState from '../root-state'

interface WithPermissionCheckOptions {
  requiredPermission: Permission | Permission[]
  requireAny?: boolean // trueの場合、いずれかの権限があればOK
  redirectTo?: string
}

/**
 * 権限チェック機能を提供するHOC
 * サイドバーに表示されない画面で直接URLアクセスを制限するために使用
 *
 * @param WrappedComponent Wrap対象のReactコンポーネント
 * @param options 権限チェックオプション
 * @returns 権限チェック機能付きのコンポーネント
 */
function withPermissionCheck<T extends {}>(
  WrappedComponent: React.ComponentType<T>,
  options: WithPermissionCheckOptions
): React.FC<T> {
  const PermissionCheckedComponent: React.FC<T> = props => {
    // Redux store からユーザーロールを取得
    const role = useSelector((state: RootState) => state.auth0.role)

    // 権限チェック実行
    let hasRequiredPermission: boolean

    if (Array.isArray(options.requiredPermission)) {
      // 複数の権限がある場合
      if (options.requireAny) {
        // いずれかの権限があればOK
        hasRequiredPermission = hasAnyPermission(role, options.requiredPermission)
      } else {
        // すべての権限が必要
        hasRequiredPermission = options.requiredPermission.every(permission =>
          hasPermission(role, permission)
        )
      }
    } else {
      // 単一の権限の場合
      hasRequiredPermission = hasPermission(role, options.requiredPermission)
    }

    if (!hasRequiredPermission) {
      // 権限がない場合はリダイレクト
      return <Redirect to={options.redirectTo || '/404'} />
    }

    // 権限がある場合は元のコンポーネントを表示
    return <WrappedComponent {...props} />
  }

  return PermissionCheckedComponent
}

export default withPermissionCheck
